/**
 * useGridSelection
 *
 * Single-select row selection for the List & Grid Standard (multi-select/bulk is NOT
 * part of the standard). Apply `rowClassName` with `appearance="none"` on the
 * `<TableRow>` — never Fluent's `appearance="brand"`. Row click selects; Enter selects;
 * double-click activates (edit) only when the caller says the row can activate.
 * See `.github/context/list-grid-standard-behaviours.context.md` §12.
 */
import type { KeyboardEvent } from 'react';
import { useCallback, useState } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';

const useSelectionStyles = makeStyles({
  selectedRow: {
    borderLeft: `${tokens.strokeWidthThicker} solid ${tokens.colorBrandBackground}`,
    '& td': {
      backgroundColor: `color-mix(in srgb, ${tokens.colorBrandBackground} 30%, transparent)`,
    },
    ':hover td': {
      backgroundColor: `color-mix(in srgb, ${tokens.colorBrandBackground} 38%, transparent)`,
    },
  },
});

export interface GridRowProps {
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent) => void;
  onDoubleClick: () => void;
  'aria-selected': boolean;
  appearance: 'none';
  className: string;
}

export interface UseGridSelectionResult<TId> {
  selectedId: TId | null;
  isSelected: (id: TId) => boolean;
  select: (id: TId) => void;
  clearSelection: () => void;
  /** The bordered/tinted style class — merge with any per-row className. */
  selectedRowClassName: string;
  /**
   * Spreadable props for a `<TableRow>`. `canActivate` gates double-click -> onActivate
   * (navigation/edit) — never enabled for a read-only user.
   */
  rowProps: (
    id: TId,
    activation?: { canActivate?: boolean; onActivate?: (id: TId) => void }
  ) => GridRowProps;
}

export function useGridSelection<TId = string>(
  onSelectionChange?: (id: TId | null) => void
): UseGridSelectionResult<TId> {
  const styles = useSelectionStyles();
  const [selectedId, setSelectedId] = useState<TId | null>(null);

  const select = useCallback(
    (id: TId) => {
      setSelectedId(id);
      onSelectionChange?.(id);
    },
    [onSelectionChange]
  );

  const clearSelection = useCallback(() => {
    setSelectedId(null);
    onSelectionChange?.(null);
  }, [onSelectionChange]);

  const isSelected = useCallback((id: TId) => selectedId === id, [selectedId]);

  const rowProps = useCallback(
    (
      id: TId,
      activation?: { canActivate?: boolean; onActivate?: (id: TId) => void }
    ): GridRowProps => ({
      onClick: () => select(id),
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'Enter') select(id);
      },
      onDoubleClick: () => {
        if (activation?.canActivate) activation.onActivate?.(id);
      },
      'aria-selected': isSelected(id),
      appearance: 'none',
      className: isSelected(id) ? styles.selectedRow : '',
    }),
    [select, isSelected, styles.selectedRow]
  );

  return { selectedId, isSelected, select, clearSelection, selectedRowClassName: styles.selectedRow, rowProps };
}
